// ItzGreen Music - Executive Intelligence Tracking Service
// Powered by PostHog Live Analytics Engine
// Captures: Geographic location, ?ref= decoder, click paths, executive identification

import posthog from 'posthog-js';

class TrackingService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.REF_STORAGE_KEY = 'itzgreen_ref_intelligence';
    this.isInitialized = false;
    this.posthogReady = false;
  }

  // Initialize PostHog + tracking engine
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Initialize PostHog SDK
      const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
      const posthogHost = process.env.REACT_APP_POSTHOG_HOST;

      if (posthogKey && posthogHost) {
        posthog.init(posthogKey, {
          api_host: posthogHost,
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: true,
          persistence: 'localStorage',
          loaded: (ph) => {
            this.posthogReady = true;
          }
        });
        this.posthogReady = true;
      }

      // Capture ?ref= parameter
      this.captureRefParam();

      // Track session start
      this.track('session_start', {
        path: window.location.pathname,
        referrer: document.referrer,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language
      });

      // Resolve and sync session geography to backend
      this.resolveSessionGeography();

      // Set up visibility tracking
      this.setupVisibilityTracking();

      this.isInitialized = true;
    } catch (error) {
      console.error('Tracking initialization failed:', error);
    }
  }

  generateSessionId() {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Primary tracking method — sends to PostHog + backend
  async track(eventType, eventData = {}) {
    const payload = {
      eventType,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      ...eventData
    };

    // Send to PostHog via SDK
    if (this.posthogReady) {
      try {
        posthog.capture(eventType, {
          ...eventData,
          session_id: this.sessionId,
          source: 'itzgreen_tracker'
        });
      } catch {
        // SDK failed — use direct API fallback
        this.postHogDirectCapture(eventType, eventData);
      }
    } else {
      // SDK not ready — use direct API
      this.postHogDirectCapture(eventType, eventData);
    }

    // Also send to backend API for SQLite ledger
    try {
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch {
      // Silent fail — PostHog is the primary engine now
    }
  }

  // Direct PostHog API fallback (bypasses SDK issues)
  async postHogDirectCapture(eventType, eventData = {}) {
    const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
    const posthogHost = process.env.REACT_APP_POSTHOG_HOST;
    if (!posthogKey || !posthogHost) return;

    try {
      await fetch(`${posthogHost}/capture/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: posthogKey,
          event: eventType,
          properties: {
            distinct_id: this.sessionId,
            $current_url: window.location.href,
            $pathname: window.location.pathname,
            $referrer: document.referrer,
            ...eventData,
            source: 'itzgreen_direct'
          },
          timestamp: new Date().toISOString()
        })
      });
    } catch {
      // Direct capture also failed — not critical
    }
  }

  // ═══════════════ REF LINK DECODER ═══════════════
  captureRefParam() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref');
      if (!ref) return null;

      const refEntry = {
        id: 'ref_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
        ref: ref,
        target: this.decodeRefTarget(ref),
        timestamp: new Date().toISOString(),
        path: window.location.pathname,
        fullUrl: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        city: 'Resolving...',
        region: '',
        country: '',
        org: ''
      };

      // Store locally
      const stored = this.getRefIntelligence();
      stored.unshift(refEntry);
      localStorage.setItem(this.REF_STORAGE_KEY, JSON.stringify(stored.slice(0, 100)));

      // Resolve geo
      this.resolveRefGeography(refEntry.id);

      // Track in PostHog with special properties
      if (this.posthogReady) {
        posthog.capture('ref_link_intercepted', {
          ref_code: ref,
          decoded_target: refEntry.target,
          entry_path: window.location.pathname,
          full_url: window.location.href,
          referrer: document.referrer
        });
      }

      // Track in backend
      this.track('ref_link_click', {
        ref,
        target: refEntry.target,
        path: window.location.pathname
      });

      return refEntry;
    } catch (error) {
      console.error('Ref capture failed:', error);
      return null;
    }
  }

  decodeRefTarget(ref) {
    const refLower = ref.toLowerCase();
    const knownTargets = {
      'macro': 'MACRO (Charles D. King)',
      'hoorae': 'HOORAE (Issa Rae)',
      'tylerperry': 'Tyler Perry Studios',
      'tyler_perry': 'Tyler Perry Studios',
      'blumhouse': 'Blumhouse Productions',
      'trailerpark': 'Trailer Park Group',
      'trailer_park': 'Trailer Park Group',
      'lionsgate': 'Lionsgate Entertainment',
      'a24': 'A24 Films',
      'hbo': 'HBO / Warner Bros Discovery',
      'netflix': 'Netflix Music',
      'hulu': 'Hulu Originals',
      'amazon': 'Amazon MGM Studios',
      'disney': 'Disney Music Group',
      'paramount': 'Paramount Global',
      'sony': 'Sony Pictures Entertainment',
      'universal': 'Universal Music Sync',
      'warner': 'Warner Music Sync',
      'fox': 'Fox Entertainment',
      'starz': 'STARZ / Lionsgate',
      'peacock': 'Peacock / NBCUniversal',
      'bet': 'BET Networks',
      'sdiazmatos': 'S. Diaz-Matos (Executive)',
      'supervisor': 'Music Supervisor (Unidentified)',
      'q2_new_assets_nijay': 'Q2 Nijay Scott Assets (Internal)',
      'wieden': 'Wieden+Kennedy',
      'wk': 'Wieden+Kennedy',
      'openx': 'OpenX Agency',
      'hartbeat': 'HARTBEAT (Kevin Hart)',
      'monkeypaw': 'Monkeypaw Productions',
      'translation': 'Translation LLC',
      'massappeal': 'Mass Appeal',
      'riot': 'Riot Games Music',
      'planb': 'Plan B Entertainment',
      'colorcreative': 'ColorCreative',
    };
    if (knownTargets[refLower]) return knownTargets[refLower];
    for (const [key, label] of Object.entries(knownTargets)) {
      if (refLower.includes(key)) return label;
    }
    return ref.toUpperCase() + ' (Unknown Target)';
  }

  async resolveRefGeography(entryId) {
    try {
      const ipResponse = await fetch('https://ipapi.co/json/');
      const ipData = await ipResponse.json();
      const city = ipData.city || 'Unknown';
      const region = ipData.region || '';
      const country = ipData.country_name || '';
      const org = ipData.org || '';

      // Update local storage
      const stored = this.getRefIntelligence();
      const entry = stored.find(e => e.id === entryId);
      if (entry) {
        entry.city = city;
        entry.region = region;
        entry.country = country;
        entry.org = org;
        localStorage.setItem(this.REF_STORAGE_KEY, JSON.stringify(stored));
      }

      // Sync geo to backend for permanent storage
      try {
        await fetch('/api/analytics/ref-geo-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            refId: entryId,
            city,
            region,
            country,
            org,
            sessionId: this.sessionId
          })
        });
      } catch {
        // Non-critical — local data is already updated
      }
    } catch {
      // Geo resolution failed — not critical
    }
  }

  getRefIntelligence() {
    try {
      return JSON.parse(localStorage.getItem(this.REF_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  getRefSummary() {
    const refs = this.getRefIntelligence();
    const refMap = {};
    refs.forEach(entry => {
      const key = entry.ref;
      if (!refMap[key]) {
        refMap[key] = { ref: entry.ref, target: entry.target, hits: 0, lastSeen: entry.timestamp, cities: [], paths: [] };
      }
      refMap[key].hits++;
      if (new Date(entry.timestamp) > new Date(refMap[key].lastSeen)) {
        refMap[key].lastSeen = entry.timestamp;
      }
      if (entry.city && entry.city !== 'Resolving...' && !refMap[key].cities.includes(entry.city)) {
        refMap[key].cities.push(entry.city);
      }
      if (!refMap[key].paths.includes(entry.path)) {
        refMap[key].paths.push(entry.path);
      }
    });
    return Object.values(refMap).sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
  }

  // Resolve and sync session geo to backend for Traffic Origin heatmap
  async resolveSessionGeography() {
    try {
      const ipResponse = await fetch('https://ipapi.co/json/');
      const ipData = await ipResponse.json();
      if (ipData.city) {
        await fetch('/api/analytics/ref-geo-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            city: ipData.city,
            region: ipData.region || '',
            country: ipData.country_name || '',
            org: ipData.org || '',
            sessionId: this.sessionId
          })
        });
      }
    } catch {
      // Non-critical
    }
  }

  // Vault access tracking
  async trackVaultAccess(accessCode, success = false) {
    if (this.posthogReady) {
      posthog.capture('vault_access', {
        success,
        access_code_provided: !!accessCode,
        session_id: this.sessionId
      });
    }
    await this.track('vault_access', {
      accessCode: accessCode ? 'provided' : 'missing',
      success,
      sessionId: this.sessionId
    });
  }

  // Email link click tracking
  async trackEmailLinkClick(source = 'unknown') {
    if (this.posthogReady) {
      posthog.capture('email_link_click', { source, referrer: document.referrer });
    }
    await this.track('email_link_click', { source, referrer: document.referrer });
  }

  // Page visibility tracking
  setupVisibilityTracking() {
    let startTime = Date.now();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        const timeSpent = Date.now() - startTime;
        this.track('page_exit', { timeSpent, path: window.location.pathname });
      } else {
        startTime = Date.now();
        this.track('page_focus', { path: window.location.pathname });
      }
    });
  }

  // Get live analytics (from backend + local ref data)
  async getLiveAnalytics() {
    try {
      let backendData = null;
      try {
        const response = await fetch('/api/analytics/live');
        if (response.ok) {
          backendData = await response.json();
        }
      } catch {
        // Backend unavailable
      }

      const refIntelligence = this.getRefIntelligence();
      const refSummary = this.getRefSummary();

      if (backendData) {
        return {
          ...backendData,
          refIntelligence: backendData.refIntelligence?.length > 0 ? backendData.refIntelligence : refIntelligence,
          refSummary: backendData.refSummary?.length > 0 ? backendData.refSummary : refSummary
        };
      }

      // Fallback to local-only data
      return {
        kpi: [
          { label: 'LIVE ACTIVE USERS', value: '1', change: '+0' },
          { label: 'TOTAL VAULT LOGINS (24H)', value: '0', change: '+0%' },
          { label: 'REF LINK HITS (ALL TIME)', value: String(refIntelligence.length), change: refIntelligence.length > 0 ? 'ACTIVE' : 'ARMED' }
        ],
        ledger: [],
        geo: [],
        refIntelligence,
        refSummary,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to fetch live analytics:', error);
      return null;
    }
  }

  // Retry failed events
  async retryFailedEvents() {
    // PostHog handles its own retry queue — no manual retry needed
  }

  // Get PostHog instance for direct access
  getPostHog() {
    return this.posthogReady ? posthog : null;
  }
}

const trackingService = new TrackingService();
export default trackingService;
